<template>
  <tr class="list-item">
    <transition name="slide">
      <td
        v-if="showMessageRemovedMovedItem"
        colspan="3"
        :class="{ 'list-item__message-container': showMessageRemovedMovedItem }"
      >
        <WishlistDetailMessageComponent />
      </td>
    </transition>
    <transition name="slide">
      <td v-if="showProduct" headers="head1" class="list-item__cell">
        <CdrGrid gutter="none" class="list-item__container-header">
          <div>
            <CdrLink :href="`/product/${productInfo.sku}`" @click="handleMetric('Product tile')">
              <picture v-if="productInfo.sku" class="list-item__image">
                <source media="(min-width: 768px)" :srcset="`/media/product/${productInfo.sku}?size=160`" />
                <CdrImg
                  v-if="productInfo.sku"
                  class="product__image"
                  :alt="productInfo.description"
                  :src="`/media/product/${productInfo.sku}?size=130`"
                />
              </picture>
            </CdrLink>
          </div>
          <div class="list-item__product">
            <CdrLink
              class="product__link"
              :href="`/product/${productInfo.sku}`"
              modifier="standalone"
              @click="handleMetric('Product tile')"
            >
              <CdrText class="product__title">
                {{ productInfo.description }}
              </CdrText>
            </CdrLink>
            <CdrText>{{ productDetails }}</CdrText>
            <CdrText class="product__code"> #{{ productInfo.sku }} </CdrText>
            <div v-if="userLoggedIn && ownedList" class="product__note">
              <div v-if="note">
                <CdrText> <span class="product__note-label"> Note: </span> {{ note }} </CdrText>
                <CdrLink
                  class="list-item__product-note"
                  modifier="standalone"
                  tag="button"
                  @click="
                    showNoteForm();
                    handleMetric('Edit note');
                  "
                >
                  Edit note
                </CdrLink>
              </div>
              <div v-else>
                <CdrLink
                  class="list-item__product-note"
                  modifier="standalone"
                  tag="button"
                  @click="
                    showNoteForm();
                    handleMetric('Add note');
                  "
                >
                  Add a note
                </CdrLink>
              </div>
              <AddNoteWishListComponent
                :note="note"
                :enabled="noteEnabled"
                :error="commentErrors"
                @hideFromButton="showNoteForm"
                @addComment="addCommentTrigger"
              />
            </div>
          </div>
        </CdrGrid>
      </td>
    </transition>
    <transition name="slide">
      <td v-if="showProduct" headers="head2" class="list-item__cell">
        <CdrGrid gutter="none" justify="right" class="list-item__container-quantity">
          <div />
          <div class="list-item__quantity">
            <CdrText v-if="!ownedList" class="product__quantity">
              {{ quantity }}
            </CdrText>
            <CdrInput
              v-if="userLoggedIn && ownedList"
              v-model="quantity"
              v-only-numbers="quantity"
              :placeholder="productInfo.quantity"
              :class="{ updated: updated, leave: leave }"
              class="product__input"
              label="Quantity"
              maxlength="4"
            />
            <div v-if="userLoggedIn && ownedList" class="list-item__quantity-actions">
              <CdrLink class="list-item__quantity-action" modifier="standalone" tag="button" @click="updateItemTrigger">
                Update
              </CdrLink>
              <CdrLink modifier="standalone" tag="button" @click="deleteItemTrigger"> Remove </CdrLink>
            </div>
          </div>
        </CdrGrid>
        <CdrBanner v-if="serviceValidation" type="error">
          <template #icon-left>
            <IconErrorFill />
          </template>
          {{ serviceValidation }}
        </CdrBanner>
      </td>
    </transition>
    <transition name="slide">
      <td v-if="showProduct" headers="head3" class="list-item__cell">
        <CdrGrid gutter="none" justify="right" class="list-item__container-price">
          <div class="list-item__price-first-cell" />
          <div class="list-item__price">
            <CdrText v-if="productInfo.available"> In stock </CdrText>
            <CdrText v-if="!productInfo.available"> No longer available </CdrText>
            <CdrText v-if="productInfo.available && productInfo.price" class="product__price">
              {{ formatToCurrency(productInfo.price) }}
            </CdrText>
          </div>
          <div class="list-item__actions">
            <CdrButton
              :disabled="!productInfo.available || loading"
              class="product__button"
              :aria-busy="loading"
              aria-haspopup="dialog"
              @click="addToCartTrigger"
            >
              <CompassSpinner v-if="loading" aria-label="Content loading" class="product__button-spinner" />
              Add to cart
            </CdrButton>
            <CdrSelect
              v-if="userLoggedIn && ownedList"
              :key="`add-to-card-${productInfo.id}`"
              v-model="listNames"
              label="Move list"
              hide-label
              prompt="Move"
              class="list-item__actions-select"
              :options="availableWishlist"
              @change="moveItemTrigger"
            />
          </div>
        </CdrGrid>
      </td>
    </transition>
  </tr>
</template>

<script>
import {
  CdrGrid,
  CdrLink,
  CdrText,
  CdrImg,
  CdrInput,
  CdrButton,
  CdrSelect,
  IconErrorFill,
  CdrBanner,
} from '@rei/cedar';
import { mapActions } from 'pinia';
import { CompassSpinner } from '@rei/spinner';
import AddNoteWishListComponent from '../common/AddNoteWishListComponent.vue';
import WishlistDetailMessageComponent from './WishlistDetailMessageComponent.vue';
import formatToCurrency from '../../common/format-currency';
import onlyNumbers from '../../directives/OnlyNumbersDirective';
import metrics from '../../utils/metrics';
import { useWishlistStore } from '../../stores';

export default {
  name: 'WishlistDetailItemComponent',
  components: {
    AddNoteWishListComponent,
    WishlistDetailMessageComponent,
    CdrGrid,
    CdrLink,
    CdrText,
    CdrImg,
    CdrInput,
    CdrButton,
    CdrSelect,
    IconErrorFill,
    CdrBanner,
    CompassSpinner,
  },
  directives: {
    onlyNumbers,
  },
  mixins: [formatToCurrency],
  props: {
    userLoggedIn: {
      type: Boolean,
      required: true,
    },
    ownedList: {
      type: Boolean,
      required: true,
    },
    productInfo: {
      type: Object,
      required: true,
    },
    userWishlists: {
      type: Array,
      default: () => [],
    },
    newGlobalNav: {
      type: Boolean,
      required: true,
    },
    modalStatus: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      listNames: '',
      noteEnabled: false,
      listId: this.productInfo.userList,
      item: this.productInfo.id,
      quantity: this.productInfo.quantity,
      sku: this.productInfo.sku,
      note: this.productInfo.userComment,
      serviceValidation: null,
      commentErrors: null,
      updated: false,
      leave: false,
      loading: false,
      description: this.productInfo.description,
      showMessageRemovedMovedItem: false,
      showProduct: true,
    };
  },
  computed: {
    availableWishlist() {
      return this.userWishlists.map((wishlist) => ({ value: wishlist.list.id, text: wishlist.list.name }));
    },
    productDetails() {
      let details = this.productInfo.color ? this.productInfo.color : '';
      details = details !== '' && this.productInfo.size ? (details += ', ') : details;
      details = this.productInfo.size ? (details += this.productInfo.size) : details;
      return details;
    },
  },
  watch: {
    modalStatus(newVa) {
      if (newVa === false) {
        this.loading = false;
      }
    },
  },
  methods: {
    ...mapActions(useWishlistStore, [
      'updateItem',
      'addComment',
      'addToCart',
      'moveItemRemovedMoved',
      'deleteItemFromBack',
      'moveItemFromBack',
    ]),
    handleMetric(metric) {
      metrics.metricsClick(metric);
    },
    showNoteForm() {
      this.noteEnabled = !this.noteEnabled;
    },
    updateItemTrigger() {
      metrics.metricsClick('Update quantity');
      this.leave = false;
      this.updated = true;
      const $this = this;
      setTimeout(() => {
        $this.leave = true;
        $this.updated = false;
      }, 1000);
      const { listId, item, quantity, note } = this;
      this.updateItem({
        listId,
        item,
        quantity,
        note,
      });
    },
    deleteItemTrigger() {
      metrics.metricsClick('Remove item');
      const { listId, item, description, sku } = this;
      const itemRemovedMoved = {
        listId,
        item,
        description,
        sku,
      };
      this.moveItemRemovedMoved({
        itemRemovedMoved,
      });
      this.deleteItemFromBack(itemRemovedMoved);
      this.transitionProduct();
    },
    addCommentTrigger(note) {
      this.note = note;
      const { listId, item } = this;
      this.addComment({
        listId,
        item,
        note,
      });
    },
    moveItemTrigger(event) {
      const newListId = event.target.value;
      const { listId, item, description, sku } = this;
      const newWishlist = this.newWishlistSelected(newListId);
      const itemRemovedMoved = {
        listId,
        item,
        description,
        sku,
        newListId,
        newListName: newWishlist.list.name,
      };
      this.moveItemRemovedMoved({
        itemRemovedMoved,
      });
      this.moveItemFromBack(itemRemovedMoved);
      this.transitionProduct();
    },
    addToCartTrigger() {
      this.loading = true;
      this.addToCart(this.productInfo)
        .then((res) => {
          if (res) {
            metrics.metricsClick('Add to cart');
            this.deleteItemTrigger();
            this.loading = false;
          }
        })
        .catch(() => {
          console.error('There was an error adding the item to the cart');
          this.loading = false;
        });
    },
    newWishlistSelected(newListId) {
      return this.userWishlists.find((wishlist) => wishlist.list.id === parseInt(newListId, 10));
    },
    transitionProduct() {
      this.showProduct = false;
      setTimeout(() => {
        this.showMessageRemovedMovedItem = true;
      }, 620);
    },
  },
};
</script>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens';

.list-item {
  p {
    margin: 0;
  }
  &__cell {
    display: block;
    padding: 0;
    border-top: 0;

    &:last-child {
      padding-bottom: $cdr-space-one-x;
    }
  }

  &__image {
    height: 100px;
    text-align: center;
    width: 100px;
  }

  &__container-header {
    grid-template-columns: 4fr 8fr;
    @include cdr-sm-mq-up {
      grid-template-columns: 2fr 10fr;
    }
  }

  &__container-quantity {
    grid-template-columns: 4fr 8fr;
    @include cdr-sm-mq-up {
      grid-template-columns: 2fr 10fr;
    }
    @include cdr-md-mq-up {
      grid-template-columns: 12fr;
    }
  }

  &__container-price {
    grid-template-columns: 4fr 8fr;
    @include cdr-sm-mq-up {
      grid-template-columns: 2fr 10fr;
    }
    @include cdr-md-mq-up {
      grid-template-columns: 4fr 8fr;
    }
  }

  &__product {
    margin-left: $cdr-space-one-x;

    &-note {
      margin-top: $cdr-space-one-x;
      margin-bottom: $cdr-space-one-x;
    }
  }

  &__quantity {
    display: flex;
    margin-left: $cdr-space-one-x;
    margin-bottom: $cdr-space-three-quarter-x;

    &-actions {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: flex-start;
      margin-left: $cdr-space-three-quarter-x;
    }

    &-action {
      margin-right: $cdr-space-three-quarter-x;

      &:first-child {
        margin-bottom: $cdr-space-quarter-x;
      }
    }
  }
  &__price-first-cell {
    @include cdr-md-mq-up {
      grid-column: span 2;
    }
  }

  &__price {
    margin-left: $cdr-space-one-x;
  }

  &__actions {
    grid-column: span 12;
    margin-top: $cdr-space-three-quarter-x;
    @include cdr-md-mq-up {
      grid-column: span 1;
    }
  }

  &__message-container {
    display: flex;
  }
}

td.list-item__cell {
  border-top: 1px solid $cdr-color-border-primary;
  padding-top: $cdr-space-one-and-a-half-x;
}

.product {
  &__title {
    @include cdr-text-utility-sans-strong-300;
    color: $cdr-color-text-primary;
    max-width: 340px;
    text-decoration: none;
  }

  &__image {
    max-height: 100px;
    max-width: 100px;
  }

  &__price {
    @include cdr-text-utility-sans-strong-300;
  }

  &__code {
    @include cdr-text-utility-sans-100;
    margin-bottom: $cdr-space-one-x;
  }

  &__button {
    margin-bottom: $cdr-space-one-x;
    &-spinner {
      width: $cdr-icon-size-lg;
      padding-right: $cdr-space-half-x;
    }
  }

  &__input {
    max-width: 75px;
  }

  &__note {
    max-width: 340px;

    p {
      word-break: break-all;
    }

    &-label {
      @include cdr-text-utility-sans-strong-400;
    }
  }
}

.slide-enter {
  opacity: 0;
}
.slide-leave-to {
  opacity: 0;
}

.slide-enter-active {
  transition: opacity 0.4s ease-in;
}
.slide-leave-active {
  transition: opacity 0.6s ease-out;
}

@media screen and (min-width: $cdr-breakpoint-sm) {
  .list-item {
    display: block;
    position: relative;

    &__quantity {
      &-actions {
        flex-direction: row;
        align-items: flex-end;
        padding-bottom: $cdr-space-three-quarter-x;
      }

      &-action {
        &:first-child {
          margin-bottom: 0;
        }
      }
    }

    &__price {
      max-width: 100px;
    }

    &__actions {
      margin-top: $cdr-space-one-and-a-half-x;
      position: absolute;
      top: 0;
      right: 100px;
      width: 153px;
    }
  }

  .product {
    &__title,
    &__price {
      @include cdr-text-utility-sans-strong-300;
    }
  }
}

@media screen and (min-width: $cdr-breakpoint-md) {
  .list-item {
    display: table-row;
    position: initial;

    &__cell {
      display: table-cell;
      border-top: 1px solid $cdr-color-border-primary;
      padding: $cdr-space-one-and-a-half-x $cdr-space-one-x;
      vertical-align: top;

      &:first-child {
        padding-left: $cdr-space-two-x;
        width: 530px;
      }
    }

    &__product {
      margin-left: $cdr-space-four-x;

      &-note {
        margin-bottom: 0;
      }
    }

    &__quantity {
      flex-direction: column;
      margin-left: 0;
      margin-bottom: 0;

      &-actions {
        flex-direction: column;
        align-items: flex-start;
        margin-left: 0;
        margin-top: $cdr-space-quarter-x;
      }

      &-action {
        margin-right: 0;
        margin-bottom: $cdr-space-quarter-x;

        &:first-child {
          margin-bottom: $cdr-space-quarter-x;
        }
      }
    }

    &__price {
      margin-left: 0;
    }

    &__actions {
      margin-top: 0;
      position: initial;
    }
    &__message-container {
      display: table-cell;
    }
  }

  .product {
    &__price {
      &#{&} {
        margin-top: $cdr-space-half-x;
      }
    }
  }

  /** removes quantity label on desktop */
  .list-item__quantity {
    label {
      display: none;
    }
  }
}
</style>
